import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { Component, Fragment } from 'react';
import cn from 'classnames';
import FlagStore from '../../stores/FlagStore';
import BoolStore from '../../stores/helpers/BoolStore';
import Icon from '../Icon/Icon';
import { Key } from '../../mirror/userInput';
import './Accordion.css';

let id = 0;
const getId = () => {
    id += 1;
    return 'Accordion_' + id;
};

class Accordion extends Component {
    static propTypes = {
        label: PropTypes.node.isRequired,
        labelWrapperClassName: PropTypes.string,
        labelClassName: PropTypes.string,
        iconClassName: PropTypes.string,
        contentClassName: PropTypes.string,
        defaultExpanded: PropTypes.bool,
        flagStore: PropTypes.oneOfType([FlagStore.PropType.isRequired, BoolStore.PropTypes.isRequired]),
        preventCollapse: PropTypes.bool,
        disableFocus: PropTypes.bool,
        qa: PropTypes.string,
    };

    state = {
        isExpanded: false
    };

    constructor(props) {
        super(props);

        const { flagStore, defaultExpanded, preventCollapse } = props;

        this.state = {
            isExpanded: flagStore ? flagStore.getValue() : !!(defaultExpanded || preventCollapse),
        };
    }

    id = getId();

    inputRef = React.createRef();
    labelRef = React.createRef();

    onLabelKeydown = (event) => {
        const { keyCode, target } = event;
        if (!this.labelRef.current || this.labelRef.current !== target) {
            return;
        }
        if (keyCode === Key.ENTER || keyCode === Key.SPACE) {
            if (this.inputRef.current) {
                event.preventDefault();
                this.inputRef.current.click();
            }
        }
    };

    onCheckboxChanged = (evt) => {
        this.setState({ isExpanded: !!evt.currentTarget.checked });
    };

    render() {
        const {
            className,
            label,
            labelWrapperClassName,
            labelClassName,
            iconClassName,
            contentClassName,
            defaultExpanded,
            flagStore,
            preventCollapse,
            disableFocus,
            children,
            qa,
            ...otherProps
        } = this.props;

        const inputProps = flagStore ? {
            checked: flagStore.getValue(),
            onChange: flagStore.toggle,
        } : {
            defaultChecked: defaultExpanded || preventCollapse,
        };

        const contentId = this.id + '_Content';
        const labelId = this.id + '_Label';

        return <div className={cn('Accordion', className)} {...otherProps}>
            <input
                aria-hidden="true"
                className="Accordion__Checkbox"
                type="checkbox"
                id={this.id}
                disabled={preventCollapse}
                ref={this.inputRef}
                onChange={this.onCheckboxChanged}
                {...inputProps}
            />
            <label
                id={labelId}
                // eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role
                role="button"
                aria-controls={contentId}
                aria-expanded={this.state.isExpanded}
                className={cn('Accordion__LabelWrapper', labelWrapperClassName)}
                tabIndex={disableFocus ? -1 : 0}
                onKeyDown={this.onLabelKeydown}
                ref={this.labelRef}
                htmlFor={this.id}
                {...qa ? { 'data-qa': qa + '-label' } : {}}
            >
                <div
                    className={cn('Accordion__Label', labelClassName)}
                    tabIndex={-1}
                >
                    {label}
                    {preventCollapse ? null : <Fragment>
                        <Icon
                            className={cn('Accordion__Expand', iconClassName)}
                            type="keyboard_arrow_down"
                        />
                        <Icon
                            className={cn('Accordion__Collapse', iconClassName)}
                            type="keyboard_arrow_up"
                        />
                    </Fragment>}
                </div>
            </label>
            <div
                className="Accordion__ContentWrapper"
                id={contentId}
                role="region"
                aria-labelledby={labelId}
                hidden={!this.state.isExpanded}
            >
                <div className={cn('Accordion__Content', contentClassName)}>
                    {children}
                </div>
            </div>
        </div>;
    }
}

export default observer(Accordion);
